import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash, Flask, ArrowDown, CaretUpDown } from "@phosphor-icons/react";
import NumberInput from "@/components/ui/NumberInput";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

function ProductoPicker({ productos, value, onChange, excludeId, testid }) {
  const [open, setOpen] = useState(false);
  const sel = productos.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="h-8 w-full border border-neutral-300 rounded-sm px-2 text-[12px] text-left flex items-center justify-between bg-white hover:bg-neutral-50">
          <span className="truncate">{sel ? sel.nombre : <span className="text-neutral-400">Elegí un producto…</span>}</span>
          <CaretUpDown size={12} className="text-neutral-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command>
          <CommandInput placeholder="Buscar producto…" className="h-9 text-[12px]" />
          <CommandList className="max-h-60">
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {productos.filter((p) => p.id !== excludeId).map((p) => (
                <CommandItem key={p.id} value={`${p.codigo || ""} ${p.nombre}`} onSelect={() => { onChange(p); setOpen(false); }} className="text-[12px]">
                  <span className="flex-1 truncate">{p.nombre}</span>
                  <span className="font-mono text-[11px] text-neutral-500 ml-2">$ {fmt(p.ultimo_costo)}/kg</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Receta de costo: costo/kg = Σ costo_kg(ingrediente) × %.
 * El % es cuántos kg del ingrediente se usan por cada kg de producto terminado (×100).
 */
export function RecetaCostoEditor({ form, setForm, productos, productoId }) {
  const receta = form.receta_costo || [];
  const setIng = (i, patch) => setForm({ ...form, receta_costo: receta.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const add = () => setForm({ ...form, receta_costo: [...receta, { producto_id: "", nombre: "", porcentaje: "" }] });
  const del = (i) => setForm({ ...form, receta_costo: receta.filter((_, j) => j !== i) });

  const calc = useMemo(() => {
    let total = 0, pct = 0, incompleto = false;
    const filas = receta.map((r) => {
      const p = productos.find((x) => x.id === r.producto_id);
      const costo = p ? Number(p.ultimo_costo) || 0 : null;
      const porc = Number(r.porcentaje) || 0;
      if (!p || !porc) incompleto = true;
      const aporte = costo != null ? (costo * porc) / 100 : 0;
      total += aporte; pct += porc;
      return { costo, aporte };
    });
    return { filas, total: Math.round(total * 100) / 100, pct: Math.round(pct * 100) / 100, incompleto };
  }, [receta, productos]);

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[#E5E5E5]" data-testid="receta-costo">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-mono text-[#666] flex items-center gap-1.5">
          <Flask size={12} weight="bold" /> Receta de costo <span className="normal-case tracking-normal">(por kg)</span>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-sm h-7 text-[11px]" data-testid="receta-add">
          <Plus size={12} className="mr-1" /> Ingrediente
        </Button>
      </div>
      {receta.length === 0 && (
        <div className="text-[11px] text-neutral-500 mt-2">Para productos elaborados: sumá los productos que lo componen y el % de cada uno por kg (ej. Milanesa = Nalga 85% + Pan rallado 15% + Huevo 5%).</div>
      )}
      <div className="space-y-1.5 mt-2">
        {receta.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_76px_96px_28px] gap-2 items-center" data-testid={`receta-ing-${i}`}>
            <ProductoPicker
              productos={productos}
              value={r.producto_id}
              excludeId={productoId}
              onChange={(p) => setIng(i, { producto_id: p.id, nombre: p.nombre })}
              testid={`receta-ing-prod-${i}`}
            />
            <div className="relative">
              <NumberInput step="0.01" min={0} value={r.porcentaje} onChange={(e) => setIng(i, { porcentaje: e.target.value })} className="rounded-sm h-8 text-[12px] pr-5 text-right" data-testid={`receta-ing-pct-${i}`} />
              <span className="absolute right-2 top-1.5 text-[11px] text-neutral-400">%</span>
            </div>
            <div className="font-mono text-[11px] text-right text-neutral-600" title={calc.filas[i]?.costo != null ? `$ ${fmt(calc.filas[i].costo)}/kg × ${r.porcentaje || 0}%` : "Sin costo"}>
              {calc.filas[i]?.costo != null ? `$ ${fmt(calc.filas[i].aporte)}` : "—"}
            </div>
            <button type="button" onClick={() => del(i)} className="p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50" data-testid={`receta-ing-del-${i}`}>
              <Trash size={13} />
            </button>
          </div>
        ))}
      </div>
      {receta.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3 bg-[#F5F1E9]/50 border border-[#6B1F24]/20 rounded-sm px-3 py-2">
          <div className="text-[11px] font-mono text-[#666]">
            Σ {fmt(calc.pct)}% {calc.pct > 100 ? <span className="text-amber-700">(incluye merma)</span> : null}
          </div>
          <div className="font-mono text-[14px] font-semibold text-[#6B1F24] ml-auto" data-testid="receta-costo-calculado">
            Costo según receta: $ {fmt(calc.total)}/kg
          </div>
          <Button
            type="button" size="sm" variant="outline"
            disabled={calc.incompleto}
            onClick={() => setForm({ ...form, ultimo_costo: calc.total })}
            className="rounded-sm h-7 text-[11px] border-[#6B1F24]/40 text-[#6B1F24] hover:bg-[#F5F1E9]"
            data-testid="receta-aplicar"
            title="Copiar el costo calculado al costo unitario"
          >
            <ArrowDown size={12} className="mr-1" /> Traer costo
          </Button>
          <label className="w-full inline-flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" className="accent-[#6B1F24]" checked={!!form.costo_por_receta} onChange={(e) => setForm({ ...form, costo_por_receta: e.target.checked, ...(e.target.checked && !calc.incompleto ? { ultimo_costo: calc.total } : {}) })} data-testid="receta-auto" />
            Costo automático por receta: se recalcula solo cuando cambia el costo de un ingrediente (compras o edición).
          </label>
        </div>
      )}
    </div>
  );
}

export function normalizarReceta(form) {
  return {
    receta_costo: (form.receta_costo || [])
      .filter((r) => r.producto_id && Number(r.porcentaje) > 0)
      .map((r) => ({ producto_id: r.producto_id, nombre: r.nombre, porcentaje: Number(r.porcentaje) })),
    costo_por_receta: !!form.costo_por_receta,
  };
}
